import { useEffect, useState, useRef } from "react";
import { api, currency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Sparkles, Loader2, ReceiptText, Upload, FileText, Pencil } from "lucide-react";

const STATUS = ["received", "approved", "paid", "disputed"];
const STATUS_STYLE = {
  received: "bg-[#5E6B78]/10 text-[#5E6B78]",
  approved: "bg-[#174A7E]/10 text-[#174A7E]",
  paid: "bg-[#156F52]/10 text-[#156F52]",
  disputed: "bg-[#B54747]/10 text-[#B54747]",
};

const empty = { vendor_id: "", invoice_number: "", period: "", amount: 0, status: "received", raw_text: "" };

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [reviewing, setReviewing] = useState(null);
  const [reviewText, setReviewText] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ vendor_id: "", period: "" });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const csvRef = useRef(null);
  const { user } = useAuth();
  const writable = can(user?.role, "invoice:write");

  const load = () => {
    api.get("/invoices").then((r) => setInvoices(r.data));
    api.get("/vendors").then((r) => setVendors(r.data));
  };
  useEffect(() => {
    load();
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const vName = (id) => vendors.find((v) => v.id === id)?.name || "—";

  const openNew = () => {
    setEditingId(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (inv) => {
    setEditingId(inv.id);
    setForm({
      vendor_id: inv.vendor_id || "",
      invoice_number: inv.invoice_number || "",
      period: inv.period || "",
      amount: inv.amount || 0,
      status: inv.status || "received",
      raw_text: inv.raw_text || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.vendor_id) return toast.error("Select a vendor");
    const payload = { ...form, amount: Number(form.amount) || 0 };
    try {
      if (editingId) {
        await api.put(`/invoices/${editingId}`, payload);
        toast.success("Invoice updated");
      } else {
        await api.post("/invoices", payload);
        toast.success("Invoice logged");
      }
      setOpen(false);
      setForm(empty);
      setEditingId(null);
      load();
    } catch {
      toast.error("Could not save the invoice");
    }
  };

  const remove = async (id) => {
    await api.delete(`/invoices/${id}`);
    load();
  };

  const uploadInvoice = async () => {
    if (!uploadForm.vendor_id) return toast.error("Select a vendor");
    if (!uploadFile) return toast.error("Choose a file");
    setUploading(true);
    const fd = new FormData();
    fd.append("vendor_id", uploadForm.vendor_id);
    fd.append("period", uploadForm.period);
    fd.append("file", uploadFile);
    try {
      await api.post("/invoices/upload", fd);
      toast.success("Uploaded & audited by AI");
      setUploadOpen(false);
      setUploadForm({ vendor_id: "", period: "" });
      setUploadFile(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/invoices/import", fd);
      toast.success(`Imported ${data.imported} invoices`);
      load();
    } catch {
      toast.error("Import failed — check CSV columns");
    }
    e.target.value = "";
  };

  const viewFile = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Could not open file");
    }
  };

  const review = async (inv) => {
    setReviewing(inv.id);
    setReviewText(null);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/review`);
      setReviewText({ vendor: vName(inv.vendor_id), number: inv.invoice_number, text: data.ai_review });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI review failed");
    } finally {
      setReviewing(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">AI-assisted</p>
          <h1 className="font-display text-4xl font-light mt-1">Invoice Reconciliation</h1>
        </div>
        {writable && <div className="flex gap-2">
          <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="invoices-csv-input" />
          <Button variant="outline" className="rounded-none" onClick={() => csvRef.current?.click()} data-testid="import-invoices-csv-button">
            <FileText className="h-4 w-4 mr-1" /> Import CSV
          </Button>
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-none border-[#174A7E] text-[#174A7E] hover:bg-blue-50" data-testid="upload-invoice-button">
                <Upload className="h-4 w-4 mr-1" /> Upload file (AI)
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Upload invoice for AI audit</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label>Vendor</Label>
                  <Select value={uploadForm.vendor_id} onValueChange={(v) => setUploadForm({ ...uploadForm, vendor_id: v })}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="upload-vendor-select">
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map((v) => (<SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Period</Label>
                  <Input value={uploadForm.period} onChange={(e) => setUploadForm({ ...uploadForm, period: e.target.value })} placeholder="May 2026" className="mt-1 rounded-none" />
                </div>
                <div>
                  <Label>Invoice file (PDF / PNG / JPG)</Label>
                  <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setUploadFile(e.target.files[0])} data-testid="upload-file-input" className="mt-1 rounded-none" />
                  <p className="text-xs text-zinc-500 mt-1">Gemini reads the file, extracts the total and audits it against the contract terms.</p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={uploadInvoice} disabled={uploading} className="bg-[#174A7E] text-white rounded-none" data-testid="submit-upload-button">
                  {uploading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reading & auditing…</>) : (<><Sparkles className="h-4 w-4 mr-2" /> Upload & audit</>)}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button className="bg-[#0B1F33] text-white rounded-none hover:bg-zinc-800" onClick={openNew} data-testid="add-invoice-button">
            <Plus className="h-4 w-4 mr-1" /> Log invoice
          </Button>
        </div>}
      </div>

      {invoices.length === 0 && (
        <div className="border border-dashed border-zinc-300 p-16 text-center text-zinc-500">
          <ReceiptText className="h-8 w-8 mx-auto mb-3 text-zinc-400" />
          No invoices logged yet.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {invoices.map((inv) => (
          <div key={inv.id} className="border border-zinc-200 bg-white p-5" data-testid={`invoice-card-${inv.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">
                  {inv.invoice_number || "no #"} · {inv.period}
                </p>
                <h3 className="font-display text-xl mt-1">{vName(inv.vendor_id)}</h3>
              </div>
              {writable && (
                <div className="flex items-center gap-2">
                  <button onClick={() => openEdit(inv)} className="text-zinc-300 hover:text-[#174A7E]" data-testid={`edit-invoice-${inv.id}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(inv.id)} className="text-zinc-300 hover:text-[#B54747]" data-testid={`delete-invoice-${inv.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mt-4">
              <span className="font-display text-3xl">{currency(inv.amount)}</span>
              <span className={`px-2 py-1 text-xs font-mono-data uppercase ${STATUS_STYLE[inv.status]}`}>{inv.status}</span>
            </div>
            {inv.storage_path && (
              <button
                onClick={() => viewFile(inv)}
                className="mt-3 text-xs font-mono-data text-[#174A7E] hover:underline flex items-center gap-1"
                data-testid={`view-file-${inv.id}`}
              >
                <FileText className="h-3 w-3" /> {inv.original_filename || "View uploaded file"}
              </button>
            )}
            {inv.ai_review && (
              <pre className="mt-4 border border-zinc-100 bg-zinc-50 p-3 text-xs whitespace-pre-wrap font-mono-data text-zinc-700 max-h-40 overflow-y-auto" data-testid={`invoice-review-${inv.id}`}>
                {inv.ai_review}
              </pre>
            )}
            {writable && (
              <Button
                onClick={() => review(inv)}
                disabled={reviewing === inv.id}
                variant="outline"
                data-testid={`review-invoice-${inv.id}`}
                className="w-full mt-4 rounded-none border-[#174A7E] text-[#174A7E] hover:bg-blue-50"
              >
                {reviewing === inv.id ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Auditing…</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" /> {inv.ai_review ? "Re-run AI audit" : "AI audit vs. contract"}</>
                )}
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Add / Edit invoice dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editingId ? "Edit invoice" : "Log vendor invoice"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label>Vendor</Label>
              <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="invoice-vendor-select">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => (<SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Invoice #</Label>
              <Input value={form.invoice_number} onChange={upd("invoice_number")} className="mt-1 rounded-none" />
            </div>
            <div>
              <Label>Period</Label>
              <Input value={form.period} onChange={upd("period")} placeholder="May 2026" className="mt-1 rounded-none" />
            </div>
            <div>
              <Label>Amount ($)</Label>
              <Input type="number" value={form.amount} onChange={upd("amount")} data-testid="invoice-amount-input" className="mt-1 rounded-none" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Invoice line-item text (for AI review)</Label>
              <Textarea value={form.raw_text} onChange={upd("raw_text")} rows={4} data-testid="invoice-text-input"
                placeholder="Paste invoice line items here…" className="mt-1 rounded-none font-mono-data text-xs" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="bg-[#0B1F33] text-white rounded-none" data-testid="save-invoice-button">
              {editingId ? "Save changes" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reviewText} onOpenChange={() => setReviewText(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#174A7E]" /> AI Audit
            </DialogTitle>
          </DialogHeader>
          <p className="font-mono-data text-xs text-zinc-500">{reviewText?.vendor} · {reviewText?.number}</p>
          <pre className="whitespace-pre-wrap font-mono-data text-sm text-zinc-800 max-h-[50vh] overflow-y-auto">
            {reviewText?.text}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
